import React, { createContext, useState, useContext, useEffect } from 'react';

// Define the context type
interface LoadingContextType {
  isLoading: boolean;
  setLoading: (isLoading: boolean) => void;
  loadingMessage: string;
  setLoadingMessage: (message: string) => void;
}

// Create context with default values
const LoadingContext = createContext<LoadingContextType>({
  isLoading: false,
  setLoading: () => {},
  loadingMessage: 'Loading...',
  setLoadingMessage: () => {}
});

/**
 * Custom hook to access loading state
 * Defined as a named function declaration for Fast Refresh compatibility
 */
export function useLoadingContext() {
  return useContext(LoadingContext);
}

/**
 * Provider component that wraps your application
 * Defined as a named function declaration for Fast Refresh compatibility
 */
export function LoadingProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading your legal updates...');
  
  // Set up a timeout to automatically hide the loading indicator after a maximum time
  useEffect(() => {
    if (!isLoading) return; // Skip if not loading
    
    const timer = setTimeout(() => {
      if (isLoading) {
        console.log('Force-completing loading state after timeout');
        setIsLoading(false);
      }
    }, 8000); // 8 seconds maximum loading time
    
    return () => clearTimeout(timer);
  }, [isLoading]);
  
  const setLoading = (loading: boolean) => {
    if (!loading) {
      // Small delay for smoother transitions
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    } else {
      setIsLoading(loading);
    }
  };
  
  return (
    <LoadingContext.Provider value={{ isLoading, setLoading, loadingMessage, setLoadingMessage }}>
      {children}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-95 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center justify-center p-8 max-w-md text-center">
            <div className="w-16 h-16 mb-4 relative">
              <div className="absolute inset-0 rounded-full border-4 border-neutral-200"></div>
              <div className="absolute inset-0 rounded-full border-t-4 border-r-4 border-primary-500 animate-spin"></div>
            </div>
            <h3 className="text-xl font-serif font-bold text-primary-600 mb-2">Legal Nest</h3>
            <p className="text-neutral-600">
              {loadingMessage}
              <span className="inline-block w-6 text-left">
                <span className="dot-animation">.</span>
                <span className="dot-animation" style={{ animationDelay: '0.2s' }}>.</span>
                <span className="dot-animation" style={{ animationDelay: '0.4s' }}>.</span>
              </span>
            </p>
          </div>
        </div>
      )}
    </LoadingContext.Provider>
  );
}